import * as React from 'react';

import {
  DataGrid,
  ListScreenLayout,
  Pagination,
  SearchField,
  Select,
  Switch,
  type ListStatsCardProps,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideAlertTriangle,
  LucidePackage,
  LucideTrendingDown,
  LucideTrendingUp,
} from 'lucide-react';

import { $remoteFacilities } from '../../_state/facilities.state';
import * as state from '../../_state/positionsList.state';
import { useTranslations } from '../../i18n/useTranslations';
import {
  enumItemPositionSortOption,
  ItemPositionRecord,
  ItemPositionSortOption,
} from '../../types/position';

import { usePositionsListColumns } from './hooks/usePositionsListColumns';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: ItemPositionSortOption;
    desc: ItemPositionSortOption;
  }
> = {
  item: {
    asc: enumItemPositionSortOption.ItemNameAsc,
    desc: enumItemPositionSortOption.ItemNameDesc,
  },
  netPosition: {
    asc: enumItemPositionSortOption.NetPositionAsc,
    desc: enumItemPositionSortOption.NetPositionDesc,
  },
  incoming: {
    asc: enumItemPositionSortOption.IncomingAsc,
    desc: enumItemPositionSortOption.IncomingDesc,
  },
  outgoing: {
    asc: enumItemPositionSortOption.OutgoingAsc,
    desc: enumItemPositionSortOption.OutgoingDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ POSITIONS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const PositionsListView = () => {
  const t = useTranslations();

  // Atoms
  const { data, isFetching, loadingState, error } = useAtomValue(
    state.$remotePositions,
  );
  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const [searchTerm, setSearchTerm] = useAtom(state.$searchTerm);
  const [selectedFacility, setSelectedFacility] = useAtom(
    state.$selectedFacility,
  );
  const [showOnlyNegative, setShowOnlyNegative] = useAtom(
    state.$showOnlyNegative,
  );
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  // Memoized values
  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );
  const positions = data?.positions ?? [];
  const statsData = data?.stats ?? {
    totalItems: 0,
    negativePositions: 0,
    totalIncoming: 0,
    totalOutgoing: 0,
  };
  const total = data?.totalCount ?? 0;
  const facilityOptions = React.useMemo(
    () => [
      { key: 'all', label: t.positionsView.filters.allFacilities },
      ...facilities.map((f) => ({ key: f.id, label: f.facilityName })),
    ],
    [facilities, t],
  );
  const emptyPanelProps = {
    title: t.positionsView.emptyState.title,
    description: <span>{t.positionsView.emptyState.subtitle}</span>,
    centered: true,
  };
  const statsColumns = React.useMemo(
    (): ListStatsCardProps['stats'] => [
      {
        key: 'totalItems',
        label: t.positionsView.summary.totalItems,
        value: statsData.totalItems,
        icon: <LucidePackage />,
        color: 'primary',
      },
      {
        key: 'negativePositions',
        label: t.positionsView.summary.negativePositions,
        value: statsData.negativePositions,
        icon: <LucideAlertTriangle />,
        color: 'warning',
      },
      {
        key: 'totalIncoming',
        label: t.positionsView.summary.totalIncoming,
        value: statsData.totalIncoming.toLocaleString(),
        icon: <LucideTrendingUp />,
        color: 'success',
      },
      {
        key: 'totalOutgoing',
        label: t.positionsView.summary.totalOutgoing,
        value: statsData.totalOutgoing.toLocaleString(),
        icon: <LucideTrendingDown />,
        color: 'failure',
      },
    ],
    [statsData, t],
  );

  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumItemPositionSortOption.NetPositionAsc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  // Other data hooks
  const columns = usePositionsListColumns({
    facilities,
  });

  return (
    <ListScreenLayout
      className="@PositionsListView"
      filters={
        <>
          <SearchField
            autoFocus
            value={searchTerm ?? ''}
            onChange={(value) => {
              setSearchTerm(value);
              setOffset(0);
            }}
            placeholder={t.positionsView.filters.search}
            className="w-64"
          />
          <Select
            value={selectedFacility || 'all'}
            onChange={(key) => {
              setSelectedFacility(key === 'all' ? undefined : String(key));
              setOffset(0);
            }}
            options={facilityOptions}
            className="w-48"
          />
          <Switch
            isSelected={showOnlyNegative ?? false}
            onChange={(isSelected) => {
              setShowOnlyNegative(isSelected);
              setOffset(0);
            }}
            className="flex items-center gap-2"
          >
            {t.positionsView.filters.showNegativeOnly}
          </Switch>
        </>
      }
      stats={statsColumns}
      footer={
        total > limit ? (
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        ) : undefined
      }
    >
      <DataGrid<ItemPositionRecord>
        isLoading={loadingState.isInitialLoading || isFetching}
        columns={columns}
        dataSource={positions}
        rowKey="id"
        aria-label={t.positionsView.table.aria}
        selectionMode="none"
        onColumnSort={handleColumnSort}
        className="w-full text-sm"
        fullWidth
        hasError={!!error}
        errorPanelProps={
          error
            ? {
                description: t.positionsView.content.error(error.message),
                centered: true,
              }
            : undefined
        }
        emptyPanelProps={emptyPanelProps}
      />
    </ListScreenLayout>
  );
};
